/**
 * Draft Template Card Component
 *
 * Displays the full template preview with all sections.
 * Uses the same layout as note sections for consistency.
 */

import React, { useCallback } from 'react';
import { NoteSectionContainer } from '@features/note-ai-edit/components/layouts/NoteSectionContainer';
import { Segment as SectionIcon } from '@mui/icons-material';
import { Box, Chip, CircularProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import { useAppSelector } from '@store/hooks';

import { selectTaskId } from '../store/selectors';
import type { DraftTemplate } from '../types';

import { SectionPreviewCard } from './SectionPreviewCard';

interface DraftTemplateCardProps {
  /** Draft template data */
  template: DraftTemplate;
  /** Currently selected section ID */
  selectedSectionId: string | null;
  /** Callback when a section is clicked */
  onSectionClick?: (sectionId: string) => void;
  /** Whether previews are still being generated (structure_ready state) */
  isGeneratingPreviews?: boolean;
}

/**
 * Draft Template Card - Full template preview with all sections
 */
export const DraftTemplateCard: React.FC<DraftTemplateCardProps> = ({
  template,
  selectedSectionId,
  onSectionClick,
  isGeneratingPreviews = false,
}) => {
  const theme = useTheme();
  const { templateBuild } = useEventTracking();
  const taskId = useAppSelector(selectTaskId);

  // Sort sections by order
  const sortedSections = [...template.sections].sort((a, b) => a.order - b.order);

  // Handle section click with tracking
  const handleSectionClick = useCallback(
    (sectionId: string, sectionTitle: string) => {
      if (taskId) {
        templateBuild.sectionSelected(taskId, {
          section_id: sectionId,
          section_title: sectionTitle,
        });
      }
      onSectionClick?.(sectionId);
    },
    [taskId, templateBuild, onSectionClick],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      {/* Template Title and Description */}
      {(template.title || template.description) && (
        <NoteSectionContainer sx={{ mb: 4 }}>
          {template.title && (
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: template.description ? 1 : 0,
              }}
            >
              {template.title}
            </Typography>
          )}
          {template.description && (
            <Typography variant="body1" color="text.secondary">
              {template.description}
            </Typography>
          )}
        </NoteSectionContainer>
      )}

      {/* Generating Previews Indicator */}
      {isGeneratingPreviews && (
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
          <Chip
            icon={<CircularProgress size={14} sx={{ color: 'inherit' }} />}
            label="Generating section previews..."
            size="small"
            sx={{
              bgcolor: theme.palette.info.main + '15',
              color: theme.palette.info.dark,
              fontWeight: 500,
              '& .MuiChip-icon': {
                color: theme.palette.info.dark,
              },
            }}
          />
        </Box>
      )}

      {/* Sections List */}
      {sortedSections.length === 0 ? (
        <NoteSectionContainer>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 6,
              color: theme.palette.text.secondary,
            }}
          >
            <SectionIcon sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              No sections yet. Chat with the AI to add sections.
            </Typography>
          </Box>
        </NoteSectionContainer>
      ) : (
        <>
          {sortedSections.map((section, index) => (
            <SectionPreviewCard
              key={section.id}
              section={section}
              index={index}
              isSelected={selectedSectionId === section.id}
              onClick={() => handleSectionClick(section.id, section.title)}
            />
          ))}
        </>
      )}
    </Box>
  );
};

